import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const BrandingContext = createContext({});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const { user } = useAuth();

  const tenantId = user?.tenant_id || "default";

  const fetchBranding = useCallback(async (tid) => {
    try {
      const r = await api.get(`/branding/public?tenant_id=${tid || "default"}`);
      setBranding(r.data);
      applyColors(r.data);
    } catch {
      // Fallback: try default if tenant-specific fails
      if (tid && tid !== "default") {
        try {
          const r = await api.get("/branding/public?tenant_id=default");
          setBranding(r.data);
          applyColors(r.data);
        } catch {}
      }
    }
  }, []);

  // Re-fetch when user/tenant changes
  useEffect(() => {
    fetchBranding(tenantId);
  }, [tenantId, fetchBranding]);

  const applyColors = (b) => {
    if (!b) return;
    const root = document.documentElement;
    if (b.primary_color) {
      const hsl = hexToHSL(b.primary_color);
      if (hsl) root.style.setProperty("--sidebar-bg", b.primary_color);
    }
    if (b.accent_color) {
      const hsl = hexToHSL(b.accent_color);
      if (hsl) {
        root.style.setProperty("--accent-color", b.accent_color);
        root.style.setProperty("--primary", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      }
    }
  };

  const refreshBranding = useCallback(async () => {
    await fetchBranding(tenantId);
  }, [tenantId, fetchBranding]);

  return (
    <BrandingContext.Provider value={{ branding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

function hexToHSL(hex) {
  hex = hex.replace("#", "");
  if (hex.length !== 6) return null;
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export default BrandingProvider;
